import { ESCALATE_AFTER_DAYS } from "./thresholds";
import { KIND_META, PRIORITY_RANK } from "./kind-meta";
import { daysBetween } from "./time";
import type {
  BrainCategory,
  BrainItem,
  BrainItemState,
  BrainKind,
  BrainPriority,
  BrainScope,
  BrainText,
  DeckCard,
} from "./types";

const ESCALATE: Record<BrainPriority, BrainPriority> = {
  low: "normal",
  normal: "high",
  high: "urgent",
  urgent: "urgent",
};

export function derivePriority(
  kind: BrainKind,
  since: string | null,
  now: Date,
  override: BrainPriority | null
): { level: BrainPriority; reason: BrainText; manual: boolean } {
  if (override) {
    return {
      level: override,
      reason: { key: "brain.priority.manual", fallback: "Set by an admin" },
      manual: true,
    };
  }
  const meta = KIND_META[kind];
  const age = daysBetween(since, now);
  if (age >= ESCALATE_AFTER_DAYS && meta.priority !== "urgent") {
    return {
      level: ESCALATE[meta.priority],
      reason: {
        key: "brain.priority.escalated",
        fallback: "Open 10 days or more",
      },
      manual: false,
    };
  }
  return { level: meta.priority, reason: meta.reason, manual: false };
}

/** `since` as epoch ms for sorting; unknown or unparseable sorts last. */
function sinceMs(since: string | null): number {
  const t = since ? Date.parse(since) : NaN;
  return Number.isNaN(t) ? Infinity : t;
}

export interface DeckInput {
  items: BrainItem[];
  states: Map<string, BrainItemState>;
  /** item_key → note, for my acknowledgements today. */
  acknowledged: Map<string, string | null>;
  me: string;
  now: Date;
  /**
   * Staff ids (the admin roster). An assignee outside it — someone no longer
   * staff — would hide the card from everyone, so it reads as Everyone.
   * Undefined (roster loading or failed) = trust every stored assignee.
   */
  knownAssignees?: ReadonlySet<string>;
}

export interface Deck {
  cards: DeckCard[];
  acknowledged: DeckCard[];
  counts: Record<BrainCategory | "all", number>;
}

export function buildDeck({
  items,
  states,
  acknowledged,
  me,
  now,
  knownAssignees,
}: DeckInput): Deck {
  const cards: DeckCard[] = [];
  const acked: DeckCard[] = [];
  for (const item of items) {
    const st = states.get(item.key);
    const stored =
      item.sourceAssigneeId !== undefined
        ? item.sourceAssigneeId
        : (st?.assignee_id ?? null);
    const assigneeId =
      stored && knownAssignees && !knownAssignees.has(stored) ? null : stored;
    // My deck = assigned to me + Everyone's. Someone else's cards are theirs.
    if (assigneeId && assigneeId !== me) continue;
    const p = derivePriority(
      item.kind,
      item.since,
      now,
      st?.priority_override ?? null
    );
    const card: DeckCard = {
      ...item,
      category: KIND_META[item.kind].category,
      priority: p.level,
      priorityReason: p.reason,
      manualPriority: p.manual,
      assigneeId,
      assignNote: st?.assign_note ?? null,
      ageDays: daysBetween(item.since, now),
      lastEmailedAt: st?.last_emailed_at ?? null,
    };
    if (acknowledged.has(item.key)) {
      acked.push({ ...card, ackNote: acknowledged.get(item.key) ?? null });
    } else {
      cards.push(card);
    }
  }
  cards.sort((a, b) => {
    const pr = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    if (pr) return pr;
    const mine = Number(b.assigneeId === me) - Number(a.assigneeId === me);
    if (mine) return mine;
    const w = KIND_META[a.kind].weight - KIND_META[b.kind].weight;
    if (w) return w;
    const age = b.ageDays - a.ageDays;
    if (age) return age;
    // Stable across refetches: earliest `since` first (unknown last), then key.
    const sa = sinceMs(a.since);
    const sb = sinceMs(b.since);
    if (sa !== sb) return sa < sb ? -1 : 1;
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });
  const counts = {
    all: cards.length,
    payments: 0,
    tenants: 0,
    owners: 0,
    properties: 0,
    support: 0,
  };
  for (const c of cards) counts[c.category] += 1;
  return { cards, acknowledged: acked, counts };
}

export function filterDeck(
  cards: DeckCard[],
  category: BrainCategory | "all",
  scope: BrainScope,
  me: string
): DeckCard[] {
  return cards.filter(
    (c) =>
      (category === "all" || c.category === category) &&
      (scope === "all" ||
        (scope === "mine" ? c.assigneeId === me : c.assigneeId === null))
  );
}

/** Position to show after returning from a deep link: same card, else same slot. */
export function resolveReturnIndex(
  cards: readonly { key: string }[],
  itemKey: string | null,
  lastIndex: number
): number {
  if (!cards.length) return 0;
  const found = itemKey ? cards.findIndex((c) => c.key === itemKey) : -1;
  if (found >= 0) return found;
  return Math.min(Math.max(lastIndex, 0), cards.length - 1);
}

/**
 * Where "Back to colivIQ" goes from a screen opened by `withBrainReturn`;
 * null when the screen was not opened from Brain.
 */
export function brainReturnHref(params: URLSearchParams): string | null {
  if (params.get("from") !== "brain") return null;
  const item = params.get("item");
  return item
    ? `/admin/brain?item=${encodeURIComponent(item)}`
    : "/admin/brain";
}

export function withBrainReturn(href: string, itemKey: string): string {
  const [path, query = ""] = href.split("?");
  const params = new URLSearchParams(query);
  params.set("from", "brain");
  params.set("item", itemKey);
  return `${path}?${params.toString()}`;
}
